import * as Sentry from "@sentry/react";

import { AppThunk } from "store/store";

import { getFilterMetadata } from "api/filter";
import { MapExtent } from "api/useTownshipRangeGrid";

import { EntityKind } from "models/entityKind";

import { combineExtents } from "components/map/utils/combineExtents";

import { EMPTY_FILTER_ID } from "../../../constants/app.constants";
import { applyZoomToFilterExtent as applyFacilityZoomToFilterExtent } from "./facilityFilterSlice";
import { applyZoomToFilterExtent as applyWellZoomToFilterExtent } from "./filterSlice";

type ZoomTarget = {
  boundingBox: MapExtent | undefined;
  contentHash: string;
};

// Bounding box + content hash of an already-applied filter, or undefined when
// there is nothing to zoom to (no applied filter, empty results metadata, or a
// failed fetch — reported, since the only symptom is a zoom that never happens).
async function fetchZoomTarget(
  filterId: string,
  entityKind: EntityKind
): Promise<ZoomTarget | undefined> {
  if (!filterId || filterId === EMPTY_FILTER_ID) return undefined;
  try {
    const metadataResponse = await getFilterMetadata(filterId);
    if (metadataResponse?.status !== 200 || !metadataResponse.data) return undefined;
    return {
      boundingBox: metadataResponse.data.boundingBox,
      contentHash: metadataResponse.data.contentHash
    };
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setContext("zoomToFilter", {
        filterId,
        entityKind,
        action: "getFilterMetadata"
      });
      scope.setLevel("warning");
      Sentry.captureException(err);
    });
    return undefined;
  }
}

// Zooms the map to the current filter results of every active entity kind.
// Used when the zoom-to-filter toggle is switched on, so it takes effect
// immediately instead of waiting for the next filter change.
//
// Wells and facilities get the SAME combined extent rather than their own:
// the auto-zoom effects in useMapNavigation share one zoom debounce, so two
// different extents landing together would zoom to whichever fetch happened
// to resolve first and silently drop the other. With an identical box the
// first zoom already shows both result sets and the dropped second is a no-op.
export const zoomToFilterResults = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const activeEntityKinds = state.app.activeEntityKinds;
  const [well, facility] = await Promise.all([
    activeEntityKinds.includes(EntityKind.Well)
      ? fetchZoomTarget(state.filter.filterId.id, EntityKind.Well)
      : undefined,
    activeEntityKinds.includes(EntityKind.Facility)
      ? fetchZoomTarget(state.facilityFilter.filterId.id, EntityKind.Facility)
      : undefined
  ]);

  // combineExtents needs both boxes; fall back to whichever one exists.
  const combined =
    combineExtents(well?.boundingBox, facility?.boundingBox) ??
    well?.boundingBox ??
    facility?.boundingBox;
  if (!combined) return;

  if (well?.boundingBox) {
    dispatch(applyWellZoomToFilterExtent(combined, well.contentHash));
  }
  if (facility?.boundingBox) {
    dispatch(applyFacilityZoomToFilterExtent(combined, facility.contentHash));
  }
};
